/**
 * SOP Adapter
 * Transforms SOP data between API and application layer
 */

import { BaseAdapter } from './base.adapter';
import type { SOP, VectorSyncStatus } from '@/types';
import type { SOPDto, SOPResponseDto, SOPsListResponseDto, CreateSOPDto } from '@/types';

export class SOPAdapter extends BaseAdapter {
	static toCreateRequest(data: CreateSOPDto): CreateSOPDto {
		// Sanitize only the short metadata fields — content is markdown and must
		// be sent verbatim (sanitizeTextInput strips HTML/`<` and would corrupt it),
		// matching how updateContent sends content raw.
		const meta = this.sanitizePayload({
			title: data.title.trim(),
			category: data.category.trim(),
			subcategory: data.subcategory.trim(),
			version: data.version?.trim() || undefined
		}) as Omit<CreateSOPDto, 'content'>;
		return { ...meta, content: data.content };
	}

	static fromDto(dto: SOPDto): SOP {
		return {
			id: dto._id,
			intent: dto.intent,
			status: dto.status,
			approvalStatus: dto.approvalStatus,
			workflowCreated: dto.workflowCreated,
			// workflowCreated:true always takes precedence — old records don't have workflowStatus
			// in MongoDB so Mongoose fills it with the schema default 'none', making ?? unreliable
			workflowStatus: dto.workflowCreated ? 'created' : (dto.workflowStatus ?? 'none'),
			workflowJobId: dto.workflowJobId ?? null,
			workflowError: dto.workflowError ?? null,
			workflowId: dto.workflowId ?? null,
			category: dto.category,
			subcategory: dto.subcategory,
			sopCatId: dto.sopCatId,
			createdAt: dto.createdAt,
			updatedAt: dto.updatedAt ?? dto.createdAt,
			qualityScore: dto.qualityScore ?? null,
			vectorSyncStatus: (dto.vectorSyncStatus as VectorSyncStatus | undefined) ?? 'pending',
			vectorSyncedAt: dto.vectorSyncedAt ?? null,
			vectorSyncAttemptedAt: dto.vectorSyncAttemptedAt ?? null,
			vectorSyncError: dto.vectorSyncError ?? null,
			mergeStatus: dto.mergeStatus ?? 'none',
			mergeError: dto.mergeError ?? null,
			enabled: dto.enabled ?? false,
			hasOpenDraft: dto.hasOpenDraft ?? false,
			draftRevision: dto.draftRevision ?? null,
			publishedRevision: dto.publishedRevision ?? null
		};
	}


	static fromDetailApi(dto: SOPResponseDto): { sop: SOP; previewUrl: string; draftPreviewUrl?: string } {
		return {
			sop: SOPAdapter.fromDto(dto.sop),
			previewUrl: dto.previewUrl,
			...(dto.draftPreviewUrl && { draftPreviewUrl: dto.draftPreviewUrl })
		};
	}

	static fromListApi(dto: SOPsListResponseDto): {
		sops: SOP[];
		pagination: { page: number; totalPages: number; total: number };
	} {
		return {
			sops: (dto.sops ?? []).map((s) => SOPAdapter.fromDto(s)),
			pagination: {
				page: dto.page ?? 1,
				totalPages: dto.totalPages ?? 1,
				total: dto.count ?? 0
			}
		};
	}
}
